import { PlatformMfaRenewal } from '@/components/legalControl/PlatformMfaRenewal'
import { useEffect, useRef, useState } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { Field, actionLabel } from '@/pages/vault/VaultForms'
import {
  digest,
  id,
  inspectIntake,
  operatorPreparation,
  history,
  object,
  type Data,
  type Kind,
} from '@/services/legalControl/workflow'
import { useWorkflow } from '@/services/legalControl/useWorkflow'
import { WorkflowHistory } from '@/components/legalControl/WorkflowHistory'

function SubjectReviewDetails() {
  const { user, hasPermission } = useAuth(),
    flow = useWorkflow()
  const [request, setRequest] = useState(''),
    [ordinal, setOrdinal] = useState(''),
    [intake, setIntake] = useState<Data | null>(null),
    [busy, setBusy] = useState(false),
    [error, setError] = useState('')
  const [policy, setPolicy] = useState(''),
    [policyHash, setPolicyHash] = useState(''),
    [rule, setRule] = useState(''),
    [seconds, setSeconds] = useState(''),
    [policyEvidence, setPolicyEvidence] = useState(''),
    [recordHash, setRecordHash] = useState(''),
    [expires, setExpires] = useState(''),
    [decision, setDecision] = useState(''),
    [attest, setAttest] = useState(false)
  const [evidence, setEvidence] = useState<
    Array<{
      evidence_id: string
      digest: string
      category: string
      custody_reference: string
      inspected_bytes_sha256: string
    }>
  >([])
  const [review, setReview] = useState(''),
    [acceptance, setAcceptance] = useState(''),
    [intent, setIntent] = useState(''),
    [dispatch, setDispatch] = useState(''),
    [reason, setReason] = useState('')
  const [preparationState, setPreparationState] = useState<Data | null>(null)
  const intentSelection = useRef(intent)
  intentSelection.current = intent
  const [activating, setActivating] = useState(false)
  const activationFlight = useRef(false)
  const epoch = useRef(0),
    current = useRef('')
  const identity = `${user?.id}:${user?.role}:${user?.authScope}:${user?.tenantId}`
  current.current = identity
  useEffect(() => {
    epoch.current++
    setIntake(null)
    setEvidence([])
    setAttest(false)
    setError('')
  }, [request, ordinal, identity])
  useEffect(
    () => () => {
      epoch.current++
    },
    []
  )
  useEffect(
    () => setAttest(false),
    [policy, policyHash, rule, seconds, policyEvidence, recordHash, expires]
  )
  if (
    !user ||
    user.role !== 'cml_admin' ||
    user.authScope !== 'platform' ||
    user.tenantId ||
    !hasPermission('txh.platform.legal_control.subject.review')
  )
    return <p role="alert">Use an independent platform reviewer session.</p>
  async function load() {
    if (busy) return
    const owner = identity,
      version = ++epoch.current
    setBusy(true)
    setIntake(null)
    setAttest(false)
    setError('')
    try {
      const n = Number(ordinal)
      if (!Number.isInteger(n) || n < 1) throw new Error()
      const data = await inspectIntake(id(request), n)
      if (owner !== current.current || version !== epoch.current) return
      const items = data.declared_evidence
      if (!Array.isArray(items) || !items.length || items.length > 10) throw new Error()
      setEvidence(
        items.map(item => {
          const e = object(item)
          if (typeof e.digest !== 'string' || !digest.test(e.digest)) throw new Error()
          return {
            evidence_id: id(e.evidence_id),
            digest: e.digest,
            category: '',
            custody_reference: '',
            inspected_bytes_sha256: '',
          }
        })
      )
      setIntake(data)
    } catch {
      if (owner === current.current && version === epoch.current)
        setError(
          'Current HUF intake is unavailable. Check the reference, revision, review permission and fresh MFA.'
        )
    } finally {
      if (owner === current.current && version === epoch.current) setBusy(false)
    }
  }
  function recordReview() {
    try {
      if (
        !intake ||
        !attest ||
        !['approve', 'reject'].includes(decision) ||
        ![policyHash, policyEvidence, recordHash].every(v => digest.test(v)) ||
        !/^[A-Za-z][A-Za-z0-9._-]{0,79}$/.test(rule) ||
        !Number.isInteger(Number(seconds)) ||
        Number(seconds) < 1 ||
        Number(seconds) > 2147483647
      )
        throw new Error()
      const end = new Date(expires)
      if (
        !Number.isFinite(end.getTime()) ||
        end.getTime() <= Date.now() ||
        evidence.some(
          e =>
            !/^[A-Za-z][A-Za-z0-9._-]{0,79}$/.test(e.category) ||
            e.custody_reference.trim() !== e.custody_reference ||
            e.custody_reference.length < 8 ||
            e.custody_reference.length > 256 ||
            e.inspected_bytes_sha256 !== e.digest
        )
      )
        throw new Error()
      const command = crypto.randomUUID()
      setReview(command)
      void flow.run(
        { kind: 'review', target: id(request), command, status: 'pending' },
        {
          review_id: command,
          expected_ordinal: Number(ordinal),
          policy_id: id(policy),
          policy_body_sha256: policyHash,
          rule_id: rule,
          authority_seconds: Number(seconds),
          policy_evidence_sha256: policyEvidence,
          reviewed_evidence: evidence.map(e => ({ ...e })),
          decision,
          decision_record_sha256: recordHash,
          expires_at: end.toISOString(),
          attest_policy_document_and_evidence_reviewed: true,
          acknowledge_no_controller_authority: true,
        }
      )
    } catch {
      setError(
        'Complete the exact approved policy, reviewed evidence, decision record, positive duration and future review expiry before recording.'
      )
    }
  }
  function step(kind: Kind) {
    try {
      setError('')
      if (kind === 'prepare') {
        if (
          !Number.isInteger(Number(ordinal)) ||
          Number(ordinal) < 1 ||
          Number(ordinal) > 2147483647
        )
          throw new Error()
        const command = crypto.randomUUID()
        setIntent(command)
        void flow.run(
          { kind, target: id(request), command, status: 'pending' },
          {
            intent_id: command,
            request_id: id(request),
            review_id: id(review),
            acceptance_id: id(acceptance),
            expected_ordinal: Number(ordinal),
          }
        )
        return
      }
      const target = id(intent),
        command = kind === 'issue' || kind === 'cancel' ? id(dispatch) : crypto.randomUUID()
      const body: Data = { intent_id: target, command_id: command }
      if (kind === 'reserve') setDispatch(command)
      if (kind === 'issue' || kind === 'cancel') {
        body.action = kind === 'issue' ? 'issued' : 'no_effect'
        body.reason = kind === 'issue' ? null : 'operator_cancelled'
      }
      if (kind === 'revoke') {
        if (reason.trim().length < 8 || reason.length > 512) throw new Error()
        body.reason = reason.trim()
      }
      if (kind === 'abort') body.reason = 'withdrawn'
      void flow.run({ kind, target, command, status: 'pending' }, body)
    } catch {
      setError(
        'Provide the original record references and, for revocation, a specific reason of 8–512 characters.'
      )
    }
  }
  async function inspectPreparation() {
    if (busy || flow.busy) return
    const owner = identity,
      target = intent,
      version = ++epoch.current
    setBusy(true)
    setError('')
    setPreparationState(null)
    try {
      const result = await operatorPreparation(id(target))
      if (
        current.current === owner &&
        intentSelection.current === target &&
        version === epoch.current
      )
        setPreparationState(result)
    } catch {
      if (current.current === owner && version === epoch.current)
        setError('Current preparation state unavailable;keep the original intent reference.')
    } finally {
      if (current.current === owner && version === epoch.current) setBusy(false)
    }
  }
  async function activate(resume = false) {
    if (activationFlight.current || !user) return
    activationFlight.current = true
    setActivating(true)
    const owner = identity
    try {
      setError('')
      let stage = resume ? flow.lastReference?.kind : undefined
      let target = ''
      if (resume) {
        const previous = flow.lastReference
        if (!previous) throw new Error()
        target = id(previous.kind === 'prepare' ? previous.command : previous.target)
        if (previous.kind === 'prepare' && flow.view?.state !== 'prepared') throw new Error()
        if (['issue', 'reconcile'].includes(previous.kind) && flow.view?.state !== 'issued')
          throw new Error()
        setIntent(target)
      } else {
        if (
          !Number.isInteger(Number(ordinal)) ||
          Number(ordinal) < 1 ||
          Number(ordinal) > 2147483647
        )
          throw new Error()
        target = crypto.randomUUID()
        setIntent(target)
        if (
          !(await flow.run(
            { kind: 'prepare', target: id(request), command: target, status: 'pending' },
            {
              intent_id: target,
              request_id: id(request),
              review_id: id(review),
              acceptance_id: id(acceptance),
              expected_ordinal: Number(ordinal),
            }
          ))
        )
          return
        stage = 'prepare'
      }
      const execute = async (kind: Kind, body: (command: string) => Data, forced?: string) => {
        const known = history(user.id).find(r => r.kind === kind && r.target === target)
        if (known) return flow.run(known)
        const command = forced ?? crypto.randomUUID()
        return flow.run({ kind, target, command, status: 'pending' }, body(command))
      }
      if (stage === 'prepare' || stage === 'reserve') {
        const reserved = await execute('reserve', command => ({
          intent_id: target,
          command_id: command,
        }))
        if (!reserved) return
        const original = history(user.id).find(r => r.kind === 'reserve' && r.target === target)
        if (!original) throw new Error()
        setDispatch(original.command)
        if (reserved.state === 'no_effect') throw new Error()
        if (reserved.state !== 'issued') {
          const issued = await execute(
            'issue',
            command => ({ intent_id: target, command_id: command, action: 'issued', reason: null }),
            original.command
          )
          if (!issued || issued.state !== 'issued') return
        }
        stage = 'issue'
      }
      if (stage === 'issue') {
        const settled = await execute('reconcile', command => ({
          intent_id: target,
          command_id: command,
        }))
        if (!settled || settled.state !== 'issued') return
        stage = 'reconcile'
      }
      if (stage === 'reconcile')
        await execute('project', command => ({ intent_id: target, command_id: command }))
    } catch {
      if (current.current === owner)
        setError(
          'Check the original outcome before continuing. Use current request/review/acceptance references; no-effect intents cannot activate an appointment.'
        )
    } finally {
      if (current.current === owner) {
        activationFlight.current = false
        setActivating(false)
      }
    }
  }
  return (
    <main className="space-y-6 text-text-primary">
      <h1 className="text-2xl font-semibold">HUF appointment review</h1>
      <p>
        Review the selected HUF request under an approved evidence policy. The proposed account must
        separately accept the exact reviewed terms. An appointment becomes active only after
        admission and projection are confirmed.
      </p>
      <fieldset disabled={busy || flow.busy || activating} className="card p-5 space-y-3">
        <legend className="font-semibold">Selected request</legend>
        <Field
          label="Intake reference"
          value={request}
          onChange={e => setRequest(e.target.value)}
        />
        <Field
          label="Intake revision"
          type="number"
          min={1}
          value={ordinal}
          onChange={e => setOrdinal(e.target.value)}
        />
        <button className="btn" onClick={() => void load()}>
          Inspect current HUF request
        </button>
        {intake && (
          <div className="border rounded p-3 space-y-1">
            <p className="break-all">Subject: {String(intake.subject_id)}</p>
            <p className="break-all">Proposed account: {String(intake.proposed_account_id)}</p>
            <p>
              Requested role:{' '}
              {intake.requested_role === 'representative' ? 'Representative' : 'Legal controller'}
            </p>
            <p>Capacity: {String(intake.declared_capacity)}</p>
            <ul>
              {(intake.requested_actions as string[]).map(a => (
                <li key={a}>{actionLabel(a)}</li>
              ))}
            </ul>
            <p>
              Source was observed at {String(intake.source_observed_at)}. The server checks
              eligibility again before admission.
            </p>
          </div>
        )}
      </fieldset>
      <PlatformMfaRenewal />
      <fieldset
        disabled={flow.locked || busy || activating || !intake}
        className="card p-5 space-y-3"
      >
        <legend className="font-semibold">Human evidence review</legend>
        <Field
          label="Approved policy reference"
          value={policy}
          onChange={e => setPolicy(e.target.value)}
        />
        <Field
          label="Approved policy fingerprint"
          value={policyHash}
          onChange={e => setPolicyHash(e.target.value)}
        />
        <Field label="Approved policy rule" value={rule} onChange={e => setRule(e.target.value)} />
        <Field
          label="Appointment duration (seconds)"
          type="number"
          min={1}
          value={seconds}
          onChange={e => setSeconds(e.target.value)}
        />
        <Field
          label="Policy evidence SHA-256"
          value={policyEvidence}
          onChange={e => setPolicyEvidence(e.target.value)}
        />
        {evidence.map((e, i) => (
          <div key={e.evidence_id} className="border rounded p-3 space-y-2">
            <p className="break-all">
              Evidence {i + 1}: {e.evidence_id}
            </p>
            <p className="break-all">Declared fingerprint: {e.digest}</p>
            {(['category', 'custody_reference', 'inspected_bytes_sha256'] as const).map(name => (
              <Field
                key={name}
                label={`${['Evidence category', 'Sealed custody reference', 'Inspected bytes SHA-256'][['category', 'custody_reference', 'inspected_bytes_sha256'].indexOf(name)]} ${i + 1}`}
                value={e[name]}
                onChange={event => {
                  setAttest(false)
                  setEvidence(rows =>
                    rows.map((row, n) => (n === i ? { ...row, [name]: event.target.value } : row))
                  )
                }}
              />
            ))}
          </div>
        ))}
        <label className="block">
          Review decision
          <select
            className="input w-full"
            value={decision}
            onChange={e => {
              setDecision(e.target.value)
              setAttest(false)
            }}
          >
            <option value="">Choose a decision</option>
            <option value="approve">Approve reviewed request</option>
            <option value="reject">Reject reviewed request</option>
          </select>
        </label>
        <Field
          label="Sealed decision record SHA-256"
          value={recordHash}
          onChange={e => setRecordHash(e.target.value)}
        />
        <Field
          label="Review valid until"
          type="datetime-local"
          value={expires}
          onChange={e => setExpires(e.target.value)}
        />
        <label className="flex gap-2">
          <input type="checkbox" checked={attest} onChange={e => setAttest(e.target.checked)} />I
          inspected the policy document and all declared evidence, and recorded my independent
          decision.
        </label>
        <button className="btn btn-primary" disabled={!attest} onClick={recordReview}>
          Record human review
        </button>
      </fieldset>
      <section className="card p-5 space-y-3">
        <h2 className="text-lg font-semibold">Verify and activate</h2>
        <p>
          Provide the recorded review and account acceptance references below. Activation checks the
          reviewed source, issues the decision, reconciles the result and activates the finite
          appointment. An interruption stops the workflow for original-outcome recovery.
        </p>
        <Field
          disabled={activating || flow.busy}
          label="Recorded review reference"
          value={review}
          onChange={e => setReview(e.target.value)}
        />
        <Field
          disabled={activating || flow.busy}
          label="Account acceptance reference"
          value={acceptance}
          onChange={e => setAcceptance(e.target.value)}
        />
        <button
          className="btn btn-primary"
          disabled={flow.locked || busy || activating}
          onClick={() => void activate()}
        >
          Verify and activate appointment
        </button>
        {flow.lastReference &&
          ['prepare', 'reserve', 'issue', 'reconcile'].includes(flow.lastReference.kind) && (
            <button
              className="btn"
              disabled={flow.locked || busy || activating}
              onClick={() => void activate(true)}
            >
              Continue checked appointment
            </button>
          )}
      </section>
      <details>
        <summary className="font-semibold">Review references and advanced recovery</summary>
        <fieldset disabled={flow.locked || busy || activating} className="card p-5 space-y-3">
          <legend className="font-semibold">Admission and recovery</legend>
          <button className="btn" onClick={() => step('prepare')}>
            Prepare accepted request
          </button>
          <Field
            label="Admission intent reference"
            value={intent}
            onChange={e => setIntent(e.target.value)}
          />
          <button className="btn" onClick={() => step('reserve')}>
            Reserve reviewed source
          </button>
          <Field
            label="Original source dispatch reference"
            value={dispatch}
            onChange={e => setDispatch(e.target.value)}
          />
          <div className="flex flex-wrap gap-3">
            <button className="btn" onClick={() => step('issue')}>
              Issue appointment decision
            </button>
            <button className="btn" onClick={() => step('cancel')}>
              Record no-effect cancellation
            </button>
            <button className="btn" onClick={() => step('reconcile')}>
              Reconcile original outcome
            </button>
            <button className="btn btn-primary" onClick={() => step('project')}>
              Activate verified appointment
            </button>
            <button className="btn" onClick={() => step('abort')}>
              Release cancelled preparation
            </button>
          </div>
          <Field
            label="Appointment revocation reason"
            value={reason}
            onChange={e => setReason(e.target.value)}
          />
          <button className="btn" onClick={() => step('revoke')}>
            Revoke appointment
          </button>
          <p>
            Use the same original references after an interruption. A no-effect result must be
            settled and reconciled before releasing the preparation. Existing appointments retain
            their own expiry when a policy is retired.
          </p>
        </fieldset>
        <button
          className="btn"
          disabled={busy || flow.busy || activating || !intent}
          onClick={() => void inspectPreparation()}
        >
          Check preparation and release state
        </button>
        {preparationState && preparationState.intent_id === intent && (
          <p role="status">
            Preparation: {String(preparationState.state)}. Intake held:{' '}
            {preparationState.intake_frozen ? 'yes' : 'no'}.
          </p>
        )}
      </details>
      {error && <p role="alert">{error}</p>}
      <WorkflowHistory flow={flow} />
      {flow.view && (
        <dl className="card p-4 space-y-2">
          {['review_id', 'intent_id', 'acceptance_id', 'controller_id', 'role', 'expires_at']
            .filter(k => typeof flow.view?.[k] === 'string')
            .map(k => (
              <div key={k}>
                <dt>
                  {
                    (
                      {
                        review_id: 'Review reference',
                        intent_id: 'Admission reference',
                        acceptance_id: 'Acceptance reference',
                        controller_id: 'Appointment reference',
                        role: 'Appointment role',
                        expires_at: 'Appointment expires',
                      } as Record<string, string>
                    )[k]
                  }
                </dt>
                <dd className="break-all">{String(flow.view?.[k])}</dd>
              </div>
            ))}
        </dl>
      )}
    </main>
  )
}

export function LegalSubjectReviewPage() {
  const { user } = useAuth()
  return (
    <SubjectReviewDetails key={`${user?.id}:${user?.authScope}:${user?.role}:${user?.tenantId}`} />
  )
}
